/**
 * Навигационные предупреждения NGA: чистая часть слоя — роды, цвета, имена
 * слоёв, заголовок. Укладка на карту — `navwarnLayer.ts`, запросы —
 * `useNavwarnLayer.ts`.
 *
 * Цвет — СМЫСЛ, а не айдентика: стрельбы и пуски красные на любом домене, как
 * у шкал прогноза и льда. Поэтому палитра здесь, а не в бренд-паке.
 */
import { OVERLAY_PREFIX } from './layerOrder'

/** Роды в порядке легенды — тот же список, что `src/navwarn/kinds.py`. */
export const NAVWARN_KINDS = [
  'missile', 'rocket', 'gunnery', 'exercises', 'distress', 'volcanic', 'ice', 'aids',
  'wreck', 'danger', 'cable', 'survey', 'drilling', 'hazardous', 'other',
] as const

const FIRING = new Set(['missile', 'rocket', 'gunnery', 'exercises'])

const COLORS: Record<string, string> = {
  firing: '#dc2626',
  distress: '#f97316',
  volcanic: '#b45309',
  ice: '#38bdf8',
  aids: '#16a34a',
  wreck: '#a16207',
  danger: '#ca8a04',
  cable: '#8b5cf6',
  survey: '#0891b2',
  drilling: '#64748b',
  hazardous: '#ea580c',
  other: '#94a3b8',
}

/** Префикс `nw-` стоит в `BOUNDARY_PREFIXES`: зона стрельб, пропавшая под
 *  растром поля, читалась бы как «здесь не стреляют». */
export const NAVWARN_SOURCE_ID = OVERLAY_PREFIX + 'nw-src'
export const NAVWARN_FILL_LAYER = OVERLAY_PREFIX + 'nw-fill'
export const NAVWARN_EDGE_LAYER = OVERLAY_PREFIX + 'nw-edge'
export const NAVWARN_LINE_LAYER = OVERLAY_PREFIX + 'nw-line'
export const NAVWARN_POINT_LAYER = OVERLAY_PREFIX + 'nw-point'
export const NAVWARN_SELECTED_LAYER = OVERLAY_PREFIX + 'nw-selected'
export const NAVWARN_LAYER_IDS = [
  NAVWARN_FILL_LAYER, NAVWARN_EDGE_LAYER, NAVWARN_LINE_LAYER, NAVWARN_POINT_LAYER,
  NAVWARN_SELECTED_LAYER,
] as const
/** Слои, по которым ловится клик. */
export const NAVWARN_HIT_LAYERS = [NAVWARN_FILL_LAYER, NAVWARN_LINE_LAYER, NAVWARN_POINT_LAYER]

export const isFiring = (kind: string): boolean => FIRING.has(kind)

export function navwarnColor(kind: string): string {
  if (FIRING.has(kind)) return COLORS.firing
  return COLORS[kind] ?? COLORS.other
}

/** Выражение MapLibre «род → цвет». */
export function navwarnColorExpression(): unknown[] {
  const expr: unknown[] = ['match', ['get', 'kind']]
  for (const k of NAVWARN_KINDS) expr.push(k, navwarnColor(k))
  expr.push(COLORS.other)
  return expr
}

const HEADS: Record<string, string> = { PAC: 'HYDROPAC', XII: 'NAVAREA XII', ARC: 'HYDROARC' }

/** «HYDROPAC 3058/26» — так сообщение называет NAVTEX, и так его ищут. */
export function navwarnTitle(m: { navarea: string; number: number; year: number }): string {
  return `${HEADS[m.navarea] ?? m.navarea} ${m.number}/${String(m.year % 100).padStart(2, '0')}`
}

/** Ключ каталога. «Прочее» — `misc`, а не `other`: окончание `.other` каталог
 *  читает как форму множественного числа. */
export const navwarnKindKey = (kind: string): string => {
  const k = (NAVWARN_KINDS as readonly string[]).includes(kind) ? kind : 'other'
  return `navwarn.kind.${k === 'other' ? 'misc' : k}`
}

/** Роды, встретившиеся в кадре, — по порядку легенды. */
export function presentKinds(kinds: Iterable<string>): string[] {
  const seen = new Set<string>()
  for (const k of kinds) seen.add((NAVWARN_KINDS as readonly string[]).includes(k) ? k : 'other')
  return NAVWARN_KINDS.filter(k => seen.has(k))
}
